// Плашки над документом: итог предварительный, «не хватает данных» с шагами, предупреждения словами.
//
// ## Принцип: причина и шаг, а не «что-то не так»
//
// Пробел называет, чего в документе не хватает («Сентябрь 2026, «Заря»: ведомости нет»), и рядом
// стоит кнопка, которая ведёт ровно туда, где это чинится. Кнопка не серая без права: у того, кто
// шаг сделать не может, кнопки нет вовсе, а над списком сказано, кто его делает. Серая кнопка не
// доводит нажатие до обработчика и выглядит как поломка (`.claude/rules/ui-views.md`, «Гейт без
// объяснения»).
//
// ## Один блок, а не плашка на каждый месяц
//
// До 01.10.2026 каждый пробел был отдельной плашкой высотой около 130 точек: у Дубравых таблица с
// деньгами начиналась на 1757 точке при семи пробелах, у человека с четырнадцатью на 2956, то
// есть через три экрана планшета. Теперь пробелы живут одним блоком «Не хватает данных (N)»: одна
// строка на месяц и объект, справа поле часов и шаг, а то, что у строк общее (подсказка, «кто
// это делает», что заденет пересчёт), сказано ОДИН раз над списком.
//
// ## Шаги разные, и путать их нельзя
//
// «Создать ведомость» — у ведомости объекта за месяц ещё нет строки; «Открыть табель» — часы
// вносят там, а не в ведомости; «Добавить в ведомость» — ведомость есть, человека в ней нет.
// Каждый ведёт на свой экран, с уже выбранным объектом и месяцем.

import { Link } from 'react-router';

import { RiErrorWarningLine, RiInformationLine } from '@remixicon/react';

import type {
  PayslipPeriodGap,
  PayslipPeriodStepKind,
  PayslipPeriodWarning,
} from '@/shared/api/endpoints/payslip';
import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';
import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import * as LinkButton from '@/shared/ui/link-button';

import {
  ВнестиЧасы,
  КТО_ВНОСИТ,
  КТО_ПЕРЕСЧИТЫВАЕТ,
  ПОЯСНЕНИЕ_ПЕРЕСЧЁТА,
  ПересчитатьВедомость,
  можноВнестиЧасы,
  можноПересчитать,
  причинаОтказа,
} from './ЧасыВПробеле';
import { адресШага, разобратьПодсказки, фразаПробела } from './тексты';

/** Право, без которого шаг сделать нельзя, и кто его делает: по нему объясняется отсутствие кнопки. */
const ШАГ: Readonly<
  Record<Exclude<PayslipPeriodStepKind, 'recalculate_payroll'>, { право: string; кто: string }>
> = {
  create_payroll: {
    право: 'core.payroll.edit',
    кто: 'Ведомости создаёт финансовый отдел.',
  },
  open_timesheet: {
    право: 'core.timesheet.view',
    кто: 'Часы вносит табельщик или финансовый отдел.',
  },
  add_person: {
    право: 'core.payroll.edit',
    кто: 'В ведомость человека добавляет финансовый отдел.',
  },
};

/** В каких пробелах часы вносят прямо в строке: нет табеля вовсе и часы внесены, а денег нет. */
const ЧАСЫ_ВНОСЯТ_ЗДЕСЬ: ReadonlySet<PayslipPeriodGap['kind']> = new Set([
  'no_timesheet',
  'hours_not_in_payroll',
]);

/**
 * Предупреждения сервера, которые только повторяют замечание под своим месяцем в таблице: «итог
 * больше, чем получается из строк», «цифрам нельзя верить», «выплачено больше». Сервер собирает их
 * из тех же полей блока, и показывать одно и то же над таблицей и в ней значит заставлять искать
 * разницу, которой нет.
 */
const ЗАМЕЧАНИЯ_БЛОКОВ: ReadonlySet<string> = new Set(['not_counted', 'adjustment', 'overpaid']);

/** Итог предварительный: одна плашка над числами, причины списком. Пусто — плашки нет. */
export function ПлашкаПредварительно({ причины }: { причины: readonly string[] }) {
  if (причины.length === 0) {
    return null;
  }
  return (
    <Alert.Root variant='lighter' status='warning' size='small' role='status'>
      <Alert.Icon as={RiErrorWarningLine} />
      <div className='flex flex-col gap-1'>
        <p className='text-label-sm text-text-strong-950'>Итог предварительный</p>
        {/* Причины списком с маркерами: четыре строки подряд без них читались как один абзац, а
            это четыре разные причины. */}
        <ul className='ml-4 flex list-disc flex-col gap-0.5'>
          {причины.map((причина) => (
            <li key={причина} className='text-paragraph-xs text-text-sub-600'>
              {причина}
            </li>
          ))}
        </ul>
      </div>
    </Alert.Root>
  );
}

/**
 * Кто делает шаги, которых у человека нет: одной строкой на каждый вид, без повторов.
 *
 * Строка пробела при этом молчит: кнопки нет, а фразу, одинаковую у шести строк, читать шесть раз
 * незачем. «Часы вносит…» у пробела, где часы вносят прямо в строке, не говорится: поле либо есть,
 * либо над списком сказано, кто его заполняет, и вторая фраза о том же другими словами читалась бы
 * как второе правило.
 */
function ктоДелает(пробелы: readonly PayslipPeriodGap[], права: readonly string[]): string[] {
  const фразы: string[] = [];
  const добавить = (фраза: string) => {
    if (!фразы.includes(фраза)) {
      фразы.push(фраза);
    }
  };
  for (const пробел of пробелы) {
    if (ЧАСЫ_ВНОСЯТ_ЗДЕСЬ.has(пробел.kind)) {
      const отказ = причинаОтказа(пробел, права);
      if (отказ !== null && 'право' in отказ) {
        добавить(КТО_ВНОСИТ);
      }
    }
    const шаг = пробел.step;
    if (шаг.kind === 'recalculate_payroll') {
      if (шаг.payroll_id !== null && шаг.payroll_id !== undefined && !можноПересчитать(права)) {
        добавить(КТО_ПЕРЕСЧИТЫВАЕТ);
      }
      continue;
    }
    if (адресШага(шаг) === null) {
      continue;
    }
    const правило = ШАГ[шаг.kind];
    if (can(права, правило.право)) {
      continue;
    }
    if (ЧАСЫ_ВНОСЯТ_ЗДЕСЬ.has(пробел.kind) && шаг.kind === 'open_timesheet') {
      continue;
    }
    добавить(правило.кто);
  }
  return фразы;
}

/** Всё, что у строк пробелов общее: подсказки сервера, «кто делает» и что заденет пересчёт. */
function общийТекст(
  пробелы: readonly PayslipPeriodGap[],
  права: readonly string[],
  объект: string | null,
): string[] {
  const пересчёт =
    можноПересчитать(права) &&
    пробелы.some(
      (пробел) =>
        пробел.step.kind === 'recalculate_payroll' &&
        пробел.step.payroll_id !== null &&
        пробел.step.payroll_id !== undefined,
    );
  return [
    ...(объект === null ? [] : [`Объект «${объект}».`]),
    ...разобратьПодсказки(пробелы).общие,
    ...ктоДелает(пробелы, права),
    ...(пересчёт ? [ПОЯСНЕНИЕ_ПЕРЕСЧЁТА] : []),
  ];
}

function Шаг({ пробел }: { пробел: PayslipPeriodGap }) {
  const права = useAuthStore((состояние) => состояние.permissions);
  if (пробел.step.kind === 'recalculate_payroll') {
    // Не переход, а действие со своим правом и своей кнопкой.
    return пробел.step.payroll_id === null || пробел.step.payroll_id === undefined ? null : (
      <ПересчитатьВедомость payrollId={пробел.step.payroll_id} />
    );
  }
  const правило = ШАГ[пробел.step.kind];
  const адрес = адресШага(пробел.step);
  if (адрес === null || !can(права, правило.право)) {
    // Нет шага либо нет права: кто его делает, сказано над списком, а серая кнопка молчала бы.
    return null;
  }
  if (пробел.step.kind === 'open_timesheet' && можноВнестиЧасы(пробел, права)) {
    // Обычное действие здесь «Внести часы» прямо в строке, а переход в табель остаётся
    // запасным путём: ссылкой, а не второй такой же кнопкой.
    return (
      <LinkButton.Root variant='gray' size='small' underline asChild>
        <Link to={адрес}>{пробел.step.label}</Link>
      </LinkButton.Root>
    );
  }
  return (
    <Button.Root variant='neutral' mode='stroke' size='xsmall' asChild>
      <Link to={адрес}>{пробел.step.label}</Link>
    </Button.Root>
  );
}

function СтрокаПробела({
  пробел,
  employeeId,
  личное,
  безОбъекта,
}: {
  пробел: PayslipPeriodGap;
  employeeId: string;
  /** Предложения подсказки, которые есть только у этой строки. */
  личное: readonly string[];
  /** Объект один на весь список и назван над ним: в строке остаются месяц и причина. */
  безОбъекта: boolean;
}) {
  const права = useAuthStore((состояние) => состояние.permissions);
  const отказ = причинаОтказа(пробел, права);
  const причинаСервера = отказ !== null && 'сервер' in отказ ? отказ.сервер : null;
  return (
    <li className='flex flex-col gap-1 border-t border-stroke-soft-200 py-2 first:border-t-0 first:pt-0 last:pb-0'>
      {/* Причина слева, поле и шаг справа в одной строке: на планшете в портрете они делят её
          пополам, а когда не помещаются, шаг уходит под причину целиком, а не рвётся посередине. */}
      <div className='flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5'>
        <p className='flex min-h-8 min-w-0 flex-1 basis-40 items-center text-label-sm text-text-strong-950'>
          {фразаПробела(пробел, безОбъекта)}
        </p>
        {/* Поле и кнопки на бумагу не идут: пробел там остаётся словами, без формы. */}
        <div data-печать='скрыть' className='flex flex-wrap items-center gap-x-2 gap-y-1.5'>
          {ЧАСЫ_ВНОСЯТ_ЗДЕСЬ.has(пробел.kind) && (
            <ВнестиЧасы пробел={пробел} employeeId={employeeId} />
          )}
          <Шаг пробел={пробел} />
        </div>
      </div>
      {причинаСервера !== null && (
        <p className='text-paragraph-xs text-text-sub-600'>{причинаСервера}</p>
      )}
      {личное.map((фраза) => (
        <p key={фраза} className='text-paragraph-xs text-text-sub-600'>
          {фраза}
        </p>
      ))}
    </li>
  );
}

/**
 * Месяцы, где деньги должны быть, а их нет: каждому причина словами и шаг.
 *
 * Одним блоком и списком: пробелы однородны (месяц, причина, шаг), и у трёх месяцев без табеля три
 * поля «Внести часы» и три ссылки «Открыть табель» — это строки списка, а не три одинаковых
 * действия на экране. Подсказка, сказанная дословно у нескольких месяцев, стоит один раз над
 * списком; то, что есть только у одной строки, остаётся при ней.
 */
export function Пробелы({
  пробелы,
  employeeId,
}: {
  пробелы: readonly PayslipPeriodGap[];
  employeeId: string;
}) {
  const права = useAuthStore((состояние) => состояние.permissions);
  if (пробелы.length === 0) {
    return null;
  }
  // Объект один на несколько пробелов: называется над списком, а не в каждой строке. У единственного
  // пробела объект остаётся в его строке: над списком называть там нечего.
  const объекты = new Set(пробелы.map((пробел) => пробел.site_name ?? ''));
  const единый = объекты.size === 1 ? ([...объекты][0] ?? '') : '';
  const объект = пробелы.length > 1 && единый !== '' ? единый : null;
  const общее = общийТекст(пробелы, права, объект);
  const { личные } = разобратьПодсказки(пробелы);
  return (
    <Alert.Root
      variant='lighter'
      status='warning'
      size='small'
      role='region'
      aria-label='Не хватает данных'
    >
      <Alert.Icon as={RiErrorWarningLine} />
      <div className='flex min-w-0 flex-col gap-2'>
        <div className='flex flex-col gap-0.5'>
          <h3 className='text-label-sm text-text-strong-950'>
            Не хватает данных ({пробелы.length})
          </h3>
          {общее.length > 0 && (
            // Каждая фраза отдельным элементом: так её находит и чтение с экрана по частям, и
            // проверка, что сказанное один раз стоит ровно один раз.
            <p className='text-paragraph-xs text-text-sub-600'>
              {общее.map((фраза, номер) => (
                <span key={фраза}>
                  {номер > 0 && ' '}
                  {фраза}
                </span>
              ))}
            </p>
          )}
        </div>
        <ul className='flex flex-col'>
          {пробелы.map((пробел) => (
            <СтрокаПробела
              key={`${пробел.kind}-${пробел.period_year}-${пробел.period_month}-${пробел.site_id ?? ''}`}
              пробел={пробел}
              employeeId={employeeId}
              личное={личные.get(пробел) ?? []}
              безОбъекта={объект !== null}
            />
          ))}
        </ul>
      </div>
    </Alert.Root>
  );
}

/**
 * Предупреждения сервера одним списком: что в документе нельзя принимать за окончательное.
 *
 * Те, что повторяют замечание под месяцем в таблице (`ЗАМЕЧАНИЯ_БЛОКОВ`), здесь не стоят: один и
 * тот же факт над таблицей и в ней читался как два разных. Остаются общие для документа.
 */
export function Предупреждения({
  предупреждения,
}: {
  предупреждения: readonly PayslipPeriodWarning[];
}) {
  const общие = предупреждения.filter(
    (предупреждение) => !ЗАМЕЧАНИЯ_БЛОКОВ.has(предупреждение.code),
  );
  if (общие.length === 0) {
    return null;
  }
  return (
    <Alert.Root variant='lighter' status='information' size='small' role='status'>
      <Alert.Icon as={RiInformationLine} />
      <div className='flex flex-col gap-1'>
        <p className='text-label-sm text-text-strong-950'>На что обратить внимание</p>
        <ul className='flex flex-col gap-1'>
          {общие.map((предупреждение) => (
            <li
              key={`${предупреждение.code}-${предупреждение.period_year ?? ''}-${предупреждение.period_month ?? ''}-${предупреждение.text}`}
              className='text-paragraph-xs text-text-sub-600'
            >
              {предупреждение.text}
            </li>
          ))}
        </ul>
      </div>
    </Alert.Root>
  );
}
